import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';
import { usePayslips } from '@/hooks/payslips';
import { usePayslipCache } from '@/hooks/payslips/usePayslipCache';
import { useMyProfile } from '@/hooks/profile';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { PayslipDetailDialog } from '@/components/payslips';
import { CompensationChangeApproval } from '@/components/payroll';
import { VirtualList } from '@/components/ui/virtual-list';
import { ProgressiveLoader } from '@/components/ui/progressive-loader';
import { ProgressiveLoading, PayslipTableSkeleton, ConnectionIndicator } from '@/components/ui/enhanced-loading-states';
import { ErrorBoundary, PayslipErrorBoundary } from '@/components/ui/error-boundary';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  DollarSign, 
  Clock, 
  FileCheck, 
  Eye, 
  Calendar, 
  Search, 
  Download,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { format, startOfYear, endOfYear, subDays, isWithinInterval } from 'date-fns';
import { toast } from 'sonner';

export default function Payslips() {
  const [selectedPayslipId, setSelectedPayslipId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('current');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'ytd' | 'last-30' | 'last-90'>('all');
  const [showApprovalWorkflow, setShowApprovalWorkflow] = useState(false);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: profile } = useMyProfile();
  const { data, isLoading, error, refetch } = usePayslips(profile?.id);
  const { warmCache, preloadVisiblePayslips } = usePayslipCache();
  const { currencyCode } = useWorkplaceCurrency();
  
  const payslips = data?.payslips || [];
  const apiSummary = data?.summary || { totalEarnings: 0, totalHours: 0, payslipCount: 0 };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    // Warm up cache for better performance
    if (profile?.id) {
      warmCache(profile.id);
    }
  }, [user, profile?.id, navigate, warmCache]);

  useEffect(() => {
    if (error) {
      console.error('Error fetching payslips:', error);
      toast.error('Failed to load payslips');
    }
  }, [error]);

  // Preload payslip details when hovering over rows
  const handlePayslipHover = (payslipId: string) => {
    preloadVisiblePayslips([payslipId]);
  };

  const handleViewPayslip = (payslipId: string) => {
    setSelectedPayslipId(payslipId);
  };

  // Filter payslips based on search and date filters
  const filteredPayslips = payslips.filter(payslip => {
    const matchesSearch = !searchQuery || 
      payslip.start_date.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payslip.end_date.toLowerCase().includes(searchQuery.toLowerCase());
    
    const payslipDate = new Date(payslip.end_date);
    const now = new Date();
    
    let matchesDate = true;
    switch (dateFilter) {
      case 'ytd':
        matchesDate = isWithinInterval(payslipDate, {
          start: startOfYear(now),
          end: now
        });
        break;
      case 'last-30':
        matchesDate = isWithinInterval(payslipDate, {
          start: subDays(now, 30),
          end: now
        });
        break;
      case 'last-90':
        matchesDate = isWithinInterval(payslipDate, {
          start: subDays(now, 90),
          end: now
        });
        break;
    }
    
    return matchesSearch && matchesDate;
  });

  // Compute summary from filtered payslips so cards match the visible list
  const summary = dateFilter === 'all' && !searchQuery
    ? apiSummary
    : {
        totalEarnings: filteredPayslips.reduce((sum, p) => sum + Number(p.gross_pay || 0), 0),
        totalHours: filteredPayslips.reduce((sum, p) => sum + Number(p.total_hours || 0), 0),
        payslipCount: filteredPayslips.length,
      };

  const filterLabel = dateFilter === 'ytd' ? 'YTD' : dateFilter === 'last-30' ? 'Last 30 Days' : dateFilter === 'last-90' ? 'Last 90 Days' : 'Total';

  const handleExportPayslips = () => {
    const csvContent = [
      ['Pay Period', 'Hours', 'Gross Pay', 'Net Pay', 'Generated Date'].join(','),
      ...filteredPayslips.map(payslip => [
        `${format(new Date(payslip.start_date), 'MMM dd')} - ${format(new Date(payslip.end_date), 'MMM dd, yyyy')}`,
        payslip.total_hours?.toString() || '0',
        payslip.gross_pay?.toString() || '0',
        payslip.net_pay?.toString() || '0',
        format(new Date(payslip.created_at), 'MMM dd, yyyy')
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payslips-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success("Payslips exported successfully!");
  };

  // Enhanced loading state with skeleton
  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-8">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-20" />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-20" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-7 w-24 mb-2" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent>
            <PayslipTableSkeleton rows={8} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ConnectionIndicator />
      <div className="container mx-auto p-4 sm:p-6 space-y-6 sm:space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">My Payslips</h1>
            <p className="text-muted-foreground">
              View your pay history, track earnings, and manage approvals
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowApprovalWorkflow(!showApprovalWorkflow)}
              className="flex-1 sm:flex-none"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Approvals
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPayslips}
              className="flex-1 sm:flex-none"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {showApprovalWorkflow && (
          <PayslipErrorBoundary>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Compensation Change Approvals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CompensationChangeApproval 
                  pendingChanges={[]}
                  onApprove={async () => {}}
                  onReject={async () => {}}
                  onBulkAction={async () => {}}
                  companyId={profile?.company_id || ''}
                />
              </CardContent>
            </Card>
          </PayslipErrorBoundary>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="current">Current Period</TabsTrigger>
            <TabsTrigger value="history">History & Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="current" className="space-y-6">
            <ProgressiveLoading
              isLoading={isLoading}
              error={error}
              isEmpty={payslips.length === 0}
              onRetry={refetch}
              onRefresh={refetch}
              emptyMessage="No payslips yet"
              emptyDescription="Your payslips will appear here once they're generated"
            >
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      {filterLabel} Earnings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(summary.totalEarnings)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      From {summary.payslipCount} pay periods
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {filterLabel} Hours
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {summary.totalHours.toLocaleString()} hrs
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Average {summary.totalHours > 0 ? Math.round(summary.totalHours / summary.payslipCount) : 0} hrs/period
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileCheck className="h-4 w-4" />
                      Pay Periods
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{summary.payslipCount}</div>
                    <p className="text-xs text-muted-foreground">
                      {filterLabel === 'Total' ? 'Total payslips available' : `Payslips in ${filterLabel.toLowerCase()} range`}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <CardTitle>Recent Payslips</CardTitle>
                      <CardDescription>
                        Your latest pay history with detailed breakdowns
                      </CardDescription>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by date..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-8 w-full sm:w-[200px]"
                        />
                      </div>
                      <Select value={dateFilter} onValueChange={(v) => setDateFilter(v as typeof dateFilter)}>
                        <SelectTrigger className="w-full sm:w-[150px]">
                          <SelectValue placeholder="Date range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Time</SelectItem>
                          <SelectItem value="ytd">Year to Date</SelectItem>
                          <SelectItem value="last-30">Last 30 Days</SelectItem>
                          <SelectItem value="last-90">Last 90 Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {filteredPayslips.length === 0 && payslips.length > 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No payslips match your filters.</p>
                      <Button variant="link" onClick={() => { setSearchQuery(''); setDateFilter('all'); }}>
                        Clear filters
                      </Button>
                    </div>
                  ) : filteredPayslips.length > 50 ? (
                    <VirtualList
                      items={filteredPayslips}
                      height={400}
                      itemHeight={60}
                      className="border rounded-lg"
                      renderItem={(payslip, index) => (
                        <div 
                          className="flex items-center justify-between p-4 border-b hover:bg-muted/50 cursor-pointer"
                          onMouseEnter={() => handlePayslipHover(payslip.id)}
                          onClick={() => handleViewPayslip(payslip.id)}
                        >
                          <div className="flex items-center space-x-4">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <div className="font-medium">
                                {format(new Date(payslip.start_date), 'MMM dd')} - {format(new Date(payslip.end_date), 'MMM dd, yyyy')}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {payslip.total_hours} hrs
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold">
                              {formatCurrency(payslip.gross_pay || 0)}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {format(new Date(payslip.created_at), 'MMM dd')}
                            </div>
                          </div>
                        </div>
                      )}
                    />
                  ) : (
                    <>
                    {/* Mobile card view */}
                    <div className="md:hidden space-y-3">
                      {filteredPayslips.map((payslip) => (
                        <div
                          key={payslip.id}
                          className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer"
                          onClick={() => handleViewPayslip(payslip.id)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium text-sm">
                                {format(new Date(payslip.start_date), 'MMM dd')} - {format(new Date(payslip.end_date), 'MMM dd, yyyy')}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                              {payslip.total_hours ? `${payslip.total_hours} hrs` : 'N/A'}
                            </span>
                            <span className="font-semibold">
                              {formatCurrency(payslip.gross_pay || 0)}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Generated: {format(new Date(payslip.created_at), 'MMM dd, yyyy')}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Desktop table view */}
                    <Table className="hidden md:table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Pay Period</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Gross Pay</TableHead>
                          <TableHead className="text-right">Generated</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPayslips.map((payslip) => (
                          <TableRow 
                            key={payslip.id}
                            className="hover:bg-muted/50 cursor-pointer"
                            onMouseEnter={() => handlePayslipHover(payslip.id)}
                          >
                            <TableCell className="font-medium">
                              <div className="flex items-center space-x-2">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                <span>
                                  {format(new Date(payslip.start_date), 'MMM dd')} - {format(new Date(payslip.end_date), 'MMM dd, yyyy')}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {payslip.total_hours ? `${payslip.total_hours} hrs` : 'N/A'}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(payslip.gross_pay || 0)}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {format(new Date(payslip.created_at), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewPayslip(payslip.id)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </>
                  )}
                </CardContent>
              </Card>
            </ProgressiveLoading>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Pay History Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">Advanced analytics coming soon...</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <PayslipDetailDialog
          payslipId={selectedPayslipId}
          open={selectedPayslipId !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedPayslipId(null);
          }}
        />
      </div>
    </ErrorBoundary>
  );
}